import { useState, useEffect, useCallback } from 'react';
import {
  KeyRound,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  ShieldCheck,
  LogIn,
  LogOut,
  Users,
  X,
  Building2,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useModuleStore, consumeTPAReauthAndReload } from '@/stores/module-store';
import { useAuthStore } from '@/stores/auth-store';
import { SharedAdminChooser } from '@/components/auth/shared-admin-chooser';
import { AnimatedHeight } from '@/components/ui/animated-height';
import { useOrgJoinStore } from '@/stores/org-join-store';
import type { TPACredentialField, TPAAccountTypeConfig, TPAPostValidationSetup } from '@/types/tpa';
import { openAuthPopup } from '@/lib/utils/sso';

interface CredentialEntryDialogProps {
  open: boolean;
  onClose?: () => void;
  /** Whether the dialog can be dismissed (false = must authenticate) */
  dismissible?: boolean;
}

/**
 * TPA Credential Entry Dialog.
 * Renders credential input fields based on the active TPA module's getCredentialFields().
 * Handles validation via the module's validateCredentials() and saving via saveTPACredentials().
 *
 * Supports three auth modes:
 *   1. OAuth 2.0 — "Connect with {Provider}" button that opens an OAuth popup
 *   2. Manual credentials — Traditional API key / token entry fields
 *   3. Post-validation setup — Additional step after credentials are validated
 *      (e.g. MessageBird workspace selection)
 *
 * Multi-step flow for modules with post-validation setup:
 *   Step 1: Enter credentials → validate with provider
 *   Step 2: Post-validation setup (e.g. choose workspace) → save everything
 */
export function CredentialEntryDialog({
  open,
  onClose,
  dismissible = false,
}: CredentialEntryDialogProps) {
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const saveTPACredentials = useModuleStore((s) => s.saveTPACredentials);
  const checkTPAAuth = useModuleStore((s) => s.checkTPAAuth);
  const invitedAdminList = useAuthStore((s) => s.invitedAdminList);
  const fetchInvitedAdminList = useAuthStore((s) => s.fetchInvitedAdminList);
  const userEmail = useAuthStore((s) => s.user?.email);
  const signOut = useAuthStore((s) => s.signOut);
  const orgName = useOrgJoinStore((s) => s.orgName);
  const choiceDone = useOrgJoinStore((s) => s.choiceDone);
  const isFirstSetup = !orgName && !choiceDone;

  const [values, setValues] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<'idle' | 'validating' | 'saving' | 'success' | 'error' | 'oauth-pending'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // null = user hasn't explicitly chosen yet; let admin list decide the default view
  const [userChoseCredentials, setUserChoseCredentials] = useState(false);
  const [forceShowChooser, setForceShowChooser] = useState(false);
  const [chooserStep, setChooserStep] = useState<'choose' | 'create' | 'join'>('choose');

  // ── Post-validation setup state (e.g. workspace selection) ──────────
  const [postValidationSetup, setPostValidationSetup] = useState<TPAPostValidationSetup | null>(null);
  const [selectedSetupChoice, setSelectedSetupChoice] = useState<string | null>(null);
  /** 'credentials' = step 1 (enter creds), 'post-validation' = step 2 (e.g. workspace pick) */
  const [dialogStep, setDialogStep] = useState<'credentials' | 'post-validation'>('credentials');

  // Fetch invited admin list and org choice when dialog opens (TPA auth failed).
  useEffect(() => {
    if (open && tpaModule) {
      fetchInvitedAdminList();
    }
  }, [open, tpaModule, fetchInvitedAdminList]);

  // Reset dialog state every time it reopens.
  useEffect(() => {
    if (open) {
      setStatus('idle');
      setErrorMessage(null);
      setValues({});
      setShowPasswords({});
      setPostValidationSetup(null);
      setSelectedSetupChoice(null);
      setDialogStep('credentials');
      setUserChoseCredentials(false);
      setForceShowChooser(false);
    }
  }, [open]);

  // Check if the TPA module supports OAuth
  const oauthConfig = tpaModule?.getOAuthConfig?.() ?? null;
  const hasOAuth = oauthConfig !== null;

  // ── OAuth completion detection ──────────────────────────────────────
  const setTPAAuthenticated = useModuleStore((s) => s.setTPAAuthenticated);

  const handleOAuthComplete = useCallback(async () => {
    if (status !== 'oauth-pending') return;
    setTPAAuthenticated(true);

    // Clear any cached /users/me or webhook responses that were fetched
    // with the pre-OAuth credentials. Otherwise the first post-auth call
    // (e.g. enabling incoming sync) can reuse a stale 401 and fail until
    // the user reloads the page.
    try {
      const { clearProxyCache } = await import('@/lib/api/proxy');
      clearProxyCache();
    } catch { /* non-critical */ }

    // Prime the module's auth state so subsequent API calls don't race
    // against a module that hasn't populated currentUser yet.
    try {
      await checkTPAAuth();
    } catch { /* non-critical */ }

    // Explicit OAuth completion — if this was a re-authorize from the
    // Connections page, purge the old account's caches and reload
    // (no-op when the flag isn't set, i.e. first-time auth).
    consumeTPAReauthAndReload();

    setStatus('success');
    onClose?.();
  }, [status, setTPAAuthenticated, checkTPAAuth, onClose]);

  const recheckAuth = useCallback(async () => {
    if (status !== 'oauth-pending') return;

    try {
      const isAuth = await checkTPAAuth();
      if (isAuth) {
        // NOT consuming the re-auth flag here: focus also fires when the user
        // closes the popup WITHOUT authorizing, and the old credentials still
        // verify — consuming would wipe caches + reload on a cancel. A real
        // completion is covered by the popup's postMessage (above) or, if
        // that was missed, by the boot-time purge on the next load.
        setStatus('success');
        onClose?.();
      }
    } catch {
      // Auth check failed, stay in pending state
    }
  }, [status, checkTPAAuth, onClose]);

  // Listen for postMessage from the authorization-success page (OAuth popup)
  useEffect(() => {
    if (status !== 'oauth-pending') return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'oauth-success') {
        handleOAuthComplete();
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('focus', recheckAuth);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', recheckAuth);
    };
  }, [status, handleOAuthComplete, recheckAuth]);

  // Compute account type config BEFORE the early return (safe when tpaModule is null)
  const accountTypeConfig: TPAAccountTypeConfig | null = tpaModule?.getAccountTypeOptions?.() ?? null;

  // Set default account type on first render if config exists
  // MUST be before the early return to satisfy React's rules of hooks
  useEffect(() => {
    if (accountTypeConfig && !values[accountTypeConfig.key]) {
      const defaultOption = accountTypeConfig.options.find((o) => o.default) || accountTypeConfig.options[0];
      if (defaultOption) {
        setValues((prev) => ({ ...prev, [accountTypeConfig.key]: defaultOption.value }));
      }
    }
  }, [accountTypeConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Honor `defaultValue` declared on credential fields by pre-filling the
  // form when the dialog opens. Only fills fields the user hasn't already
  // typed into. No-op for TPAs whose credential fields don't declare a
  // `defaultValue` — verified that no other in-tree TPA uses it on
  // `getCredentialFields()` (the three that use `defaultValue` set it on
  // `getExtraFormFields()`, a different compose-view system), so this
  // effect currently only affects Karix.
  useEffect(() => {
    if (!open || !tpaModule) return;
    const fields = tpaModule.getCredentialFields();
    const defaults: Record<string, string> = {};
    for (const f of fields) {
      if (f.defaultValue != null && !values[f.key]) {
        defaults[f.key] = f.defaultValue;
      }
    }
    if (Object.keys(defaults).length > 0) {
      setValues((prev) => ({ ...defaults, ...prev }));
    }
  }, [open, tpaModule]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !tpaModule) return null;

  const adminListLoading = invitedAdminList === null; // still fetching
  const hasSharedAdmins = invitedAdminList && Object.keys(invitedAdminList).length > 0;
  // Show org setup / admin chooser by default when user hasn't completed choice or explicitly requested chooser
  const showSharedAccess =
    (!userChoseCredentials && !orgName) || forceShowChooser;
  const showBackToOrg = userChoseCredentials || !!orgName;

  const allCredentialFields: TPACredentialField[] = tpaModule.getCredentialFields();
  const moduleName = tpaModule.name;
  const moduleIcon = tpaModule.icon;

  // Filter credential fields based on visibleWhen conditions
  const credentialFields = allCredentialFields.filter((field) => {
    if (!field.visibleWhen) return true;
    return values[field.visibleWhen.key] === field.visibleWhen.value;
  });

  const handleValueChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage(null);
    }
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allRequiredFilled = credentialFields.every((field) => {
    if (field.required === false) return true;
    return values[field.key]?.trim();
  });

  /**
   * Step 1: Validate credentials, then check for post-validation setup.
   *
   * Flow:
   *   1. Call tpaModule.validateCredentials(copy) to check with the provider
   *   2. If valid, check tpaModule.getPostValidationSetup?.(copy)
   *   3a. If setup step needed (e.g. workspace picker) → show Step 2 UI
   *   3b. If no setup needed → save immediately via saveTPACredentials(values)
   */
  const handleAuthorize = async () => {
    if (!allRequiredFilled) return;

    setStatus('validating');
    setErrorMessage(null);

    try {
      // Phase 1: Validate credentials with the provider (use a copy so original values stay intact)
      const credsCopy = { ...values };
      const validation = await tpaModule.validateCredentials(credsCopy);

      if (!validation.valid) {
        setStatus('error');
        setErrorMessage(validation.error || 'Invalid credentials. Please check and try again.');
        return;
      }

      // Credentials are valid. Sync any modifications made by validateCredentials
      // (e.g. it removes mbType, sets organizationId)
      setValues(credsCopy);

      // Phase 2: Check if a post-validation setup step is needed
      if (tpaModule.getPostValidationSetup) {
        setStatus('validating'); // keep spinner while fetching workspaces
        const setup = await tpaModule.getPostValidationSetup(credsCopy);

        if (setup && setup.choices.length > 0) {
          // Show workspace/setup picker (Step 2)
          setPostValidationSetup(setup);
          setSelectedSetupChoice(null);
          setDialogStep('post-validation');
          setValues(credsCopy); // sync again after setup modified creds
          setStatus('idle');
          return;
        }

        // No setup needed (e.g. single workspace auto-selected)
        setValues(credsCopy);
      }

      // Phase 3: Save credentials
      setStatus('saving');
      await saveTPACredentials(credsCopy);

      setStatus('success');
      onClose?.();
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Failed to authorize. Please check your credentials and try again.'
      );
    }
  };

  /**
   * Step 2: Save credentials with the selected post-validation choice.
   * Called when user picks a workspace/option and clicks "Save & Continue".
   */
  const handlePostValidationSave = async () => {
    if (!postValidationSetup || !selectedSetupChoice) return;

    setStatus('saving');
    setErrorMessage(null);

    try {
      // Set the selected choice into credentials
      const finalCreds = { ...values, [postValidationSetup.key]: selectedSetupChoice };

      // Save through the store (re-validates + saves + creates webhook)
      await saveTPACredentials(finalCreds);

      setStatus('success');
      onClose?.();
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Failed to save. Please try again.'
      );
    }
  };

  /** Go back from post-validation step to credentials step */
  const handleBackToCredentials = () => {
    setDialogStep('credentials');
    setPostValidationSetup(null);
    setSelectedSetupChoice(null);
    setStatus('idle');
    setErrorMessage(null);
  };

  /**
   * Initiate OAuth flow by opening the provider's OAuth URL in a popup.
   */
  const handleOAuthConnect = () => {
    if (!oauthConfig?.oauthUrl) return;

    setStatus('oauth-pending');
    setErrorMessage(null);

    const width = 600;
    const height = 700;
    const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
    const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));

    void openAuthPopup(oauthConfig.oauthUrl, `popup,width=${width},height=${height},left=${left},top=${top}`);
  };

  const isProcessing = status === 'validating' || status === 'saving' || status === 'success';
  const showPATFields = !hasOAuth || oauthConfig?.showPATFallback;

  // ── Post-Validation Setup Step (Step 2) ─────────────────────────────
  if (dialogStep === 'post-validation' && postValidationSetup) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop — non-dismissible during setup */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        {/* Dialog */}
        <div className="relative z-10 mx-4 w-full max-w-lg rounded-2xl bg-card shadow-2xl border border-border overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-b from-muted/50 to-transparent px-6 pt-6 pb-4">
            <div className="flex flex-col items-center gap-3 text-center">
              <Building2 className="h-10 w-10 text-primary" />
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {postValidationSetup.label}
                </h3>
                {postValidationSetup.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {postValidationSetup.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Workspace/Choice List */}
          <div className="px-6 py-4 space-y-2 max-h-[300px] overflow-y-auto">
            {postValidationSetup.choices.map((choice) => {
              const isSelected = selectedSetupChoice === choice.id;
              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => {
                    setSelectedSetupChoice(choice.id);
                    if (status === 'error') {
                      setStatus('idle');
                      setErrorMessage(null);
                    }
                  }}
                  disabled={isProcessing}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-card hover:border-muted-foreground/50'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                      isSelected
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/30'
                    )}
                  >
                    {isSelected && (
                      <div className="h-2 w-2 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {choice.name}
                    </div>
                    {choice.description && choice.description !== 'active' && (
                      <div className="text-xs text-muted-foreground truncate">
                        {choice.description}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Status messages */}
          {status === 'error' && errorMessage && (
            <div className="px-6 pb-2">
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="px-6 pb-2">
              <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>Configuration saved successfully!</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 bg-muted/30 border-t border-border">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToCredentials}
                disabled={isProcessing}
                className="gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>

              <Button
                size="sm"
                onClick={handlePostValidationSave}
                disabled={!selectedSetupChoice || isProcessing}
                className="min-w-[140px]"
              >
                {status === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : status === 'success' ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Saved
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Save &amp; Continue
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Credentials Step (Step 1 — default) ─────────────────────────────
  return (
    <div data-testid="tpa-credential-dialog" className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/60 backdrop-blur-sm',
          dismissible && !isFirstSetup ? 'cursor-pointer' : 'cursor-default'
        )}
        onClick={dismissible && !isFirstSetup ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="relative z-10 mx-4 w-full max-w-xl rounded-2xl bg-card shadow-2xl border border-border overflow-hidden">
        {/* Close button */}
        {showBackToOrg && !showSharedAccess && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setUserChoseCredentials(false);
              setChooserStep('choose');
              setForceShowChooser(true);
            }}
            disabled={isProcessing}
            className="absolute top-4 left-4 z-10 h-7 gap-1.5 px-2.5 text-xs"
          >
            <Building2 className="h-3.5 w-3.5" />
            Switch organization
          </Button>
        )}

        {dismissible && onClose && !isFirstSetup && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        <AnimatedHeight>
        <div key={showSharedAccess ? 'org' : 'creds'} className="animate-fade-in">
        {/* Header with provider branding — hidden during organization selection */}
        {!showSharedAccess && (
          <div className="bg-gradient-to-b from-muted/50 to-transparent px-6 pt-6 pb-4">
            <div className="flex flex-col items-center gap-3 text-center">
              {moduleIcon ? (
                <img
                  src={moduleIcon}
                  alt={moduleName}
                  className="max-h-16 max-w-[200px] object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <KeyRound className="h-10 w-10 text-primary" />
              )}
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Connect {moduleName}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {hasOAuth
                    ? `Authorize ${moduleName} to get started`
                    : `Enter your ${moduleName} credentials to get started`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading state while fetching admin list or checking org choice */}
        {adminListLoading ? (
          <div className="flex items-center justify-center py-12 px-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : showSharedAccess ? (
          /* Shared admin chooser — shown by default when user has no org choice */
          <div className="px-6 py-6">
            <SharedAdminChooser
              defaultStep={chooserStep}
              isSetup={isFirstSetup}
              onClose={() => {
                setUserChoseCredentials(true);
                setForceShowChooser(false);
              }}
            />
          </div>
        ) : (
        <>

        {/* Link back to shared access (shown only if admins exist and user chose credentials) */}
        {hasSharedAdmins && (
          <div className="px-6 pt-3 pb-0">
            <div className="flex items-center gap-2 rounded-lg bg-muted/40 border border-border p-3">
              <Users className="h-4 w-4 text-muted-foreground shrink-0" />
              <p className="text-xs text-foreground flex-1">
                You have been invited to join organization
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setUserChoseCredentials(false);
                  setChooserStep('join');
                  setForceShowChooser(true);
                }}
                className="shrink-0 text-xs"
              >
                Join Organization
              </Button>
            </div>
          </div>
        )}

        {/* OAuth Button */}
        {hasOAuth && (
          <div className="px-6 pt-4">
            <Button
              onClick={handleOAuthConnect}
              disabled={status === 'oauth-pending' || isProcessing}
              className="w-full h-11 text-sm font-medium gap-2"
              variant="default"
            >
              {status === 'oauth-pending' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Waiting for authorization...
                </>
              ) : (
                <>
                  {oauthConfig.providerIcon ? (
                    <img
                      src={oauthConfig.providerIcon}
                      alt=""
                      className="h-5 w-5 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <LogIn className="h-4 w-4" />
                  )}
                  {oauthConfig.buttonLabel}
                </>
              )}
            </Button>

            {/* OAuth pending hint */}
            {status === 'oauth-pending' && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                Complete authorization in the popup window. This dialog will close automatically.
              </p>
            )}
          </div>
        )}

        {/* Divider between OAuth and PAT */}
        {hasOAuth && showPATFields && (
          <div className="px-6 pt-4 flex items-center gap-3">
            <div className="flex-1 border-t border-border" />
            <span className="text-xs text-muted-foreground shrink-0">
              or enter credentials manually
            </span>
            <div className="flex-1 border-t border-border" />
          </div>
        )}

        {/* Credential Fields (PAT / API Key entry) */}
        {showPATFields && (
          <div className="px-6 py-4 space-y-4">
            {/* Account Type Selector (e.g. MessageBird old vs new version) */}
            {accountTypeConfig && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">{accountTypeConfig.label}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {accountTypeConfig.options.map((opt) => {
                    const isSelected = values[accountTypeConfig.key] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleValueChange(accountTypeConfig.key, opt.value)}
                        disabled={isProcessing || status === 'oauth-pending'}
                        className={cn(
                          'flex flex-col items-center gap-1 rounded-lg border-2 p-3 text-sm transition-all',
                          isSelected
                            ? 'border-primary bg-primary/5 text-foreground'
                            : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/50'
                        )}
                      >
                        <span className="font-medium">{opt.label}</span>
                        {opt.description && (
                          <span className="text-xs text-muted-foreground">{opt.description}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {credentialFields.map((field) => (
              <CredentialFieldInput
                key={field.key}
                field={field}
                value={values[field.key] || ''}
                onChange={(v) => handleValueChange(field.key, v)}
                showPassword={showPasswords[field.key] || false}
                onTogglePassword={() => togglePasswordVisibility(field.key)}
                disabled={isProcessing || status === 'oauth-pending'}
              />
            ))}

            {/* Status messages */}
            {status === 'error' && errorMessage && (
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {status === 'success' && (
              <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>Credentials verified and saved successfully!</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-muted/30 border-t border-border">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 ml-auto">
              {dismissible && !showBackToOrg && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
              )}
              {showPATFields && (
                <Button
                  size="sm"
                  onClick={handleAuthorize}
                  disabled={
                    !allRequiredFilled ||
                    isProcessing ||
                    status === 'oauth-pending'
                  }
                  className="min-w-[120px]"
                >
                  {status === 'validating' || status === 'saving' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {status === 'validating' ? 'Verifying...' : 'Saving...'}
                    </>
                  ) : status === 'success' ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Authorized
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4" />
                      Authorize
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        </>
        )}
        </div>
        </AnimatedHeight>

        {/* Current account + sign out */}
        <div className="px-6 pb-4 pt-2.5 mt-1 flex items-center justify-between rounded-b-2xl bg-muted/50 border-t border-border/50">
          <span className="text-[11px] text-muted-foreground truncate">
            {userEmail}
          </span>
          <button
            onClick={() => signOut()}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive transition-colors shrink-0 ml-2"
          >
            <LogOut className="h-3 w-3" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual credential field input with label, help link, and password toggle.
 */
function CredentialFieldInput({
  field,
  value,
  onChange,
  showPassword,
  onTogglePassword,
  disabled,
}: {
  field: TPACredentialField;
  value: string;
  onChange: (value: string) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  disabled: boolean;
}) {
  const isPassword = field.type === 'password';
  const inputType = isPassword && !showPassword ? 'password' : 'text';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          {field.label}
          {field.required !== false && (
            <span className="text-destructive ml-0.5">*</span>
          )}
        </Label>
        {field.helpUrl && (
          <a
            href={field.helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            Get here
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="relative">
        <Input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          className="pr-10"
          autoComplete="off"
        />
        {isPassword && value && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
      {field.helpText && (
        <p className="text-xs text-muted-foreground">{field.helpText}</p>
      )}
    </div>
  );
}
